import classNames from "classnames";
import {
  LINKS_COLUMN_AXIS_BRIDGE_INTO_DATEBLOCK_PX,
  LINKS_COLUMN_SUCCESSOR_AXIS_LINE_LEFT_PX,
  LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  computeSuccessorAxisLineRightInsetPx,
  computeSuccessorCrossLeftPx,
  type FocusedSuccessorLayoutOptions
} from "../../layout-constants";

const AXIS_LINE_COLOR = "border-ciq-gray-700";

export type LinksColumnAxisTitleProps = Readonly<{
  title: string;
  count?: number;
}>;

/** Formats "Predecessors" or "Predecessors (3)" when a count is supplied. */
export function formatLinksColumnTitle(
  baseTitle: string,
  count?: number
): string {
  if (count !== undefined) {
    return `${baseTitle} (${count})`;
  }
  return baseTitle;
}

/**
 * Predecessor label row: horizontal axis bridges into the dateblock and a
 * vertical cross sits at the column's right edge.
 */
export function PredecessorAxisLabelRow(props: LinksColumnAxisTitleProps) {
  const title = formatLinksColumnTitle(props.title, props.count);

  return (
    <div className="h-10 shrink-0 flex items-center pl-3 pr-0 relative overflow-visible">
      <div
        className="text-sm font-medium text-ciq-gray-800 shrink-0"
        data-testid="links-column-title"
      >
        {title}
      </div>
      <div
        className="grow ml-3 relative h-full"
        data-testid="links-column-axis-region"
      >
        <div
          className={classNames(
            "absolute top-1/2 left-0 -translate-y-1/2 h-0 border-0 border-t border-solid pointer-events-none",
            AXIS_LINE_COLOR
          )}
          style={{ right: -LINKS_COLUMN_AXIS_BRIDGE_INTO_DATEBLOCK_PX }}
          aria-hidden
          data-testid="links-column-axis-line"
        />
        <div
          className={classNames(
            "absolute top-1/2 right-0 -translate-y-1/2 w-0 h-16 border-0 border-l border-solid pointer-events-none",
            AXIS_LINE_COLOR
          )}
          aria-hidden
          data-testid="links-column-axis-vertical"
        />
      </div>
    </div>
  );
}

export type SuccessorAxisLabelRowProps = LinksColumnAxisTitleProps &
  Readonly<{
    successorAxisWidthPx?: number;
    focusedLayout?: FocusedSuccessorLayoutOptions;
  }>;

/**
 * Successor label row: horizontal axis spans from the dateblock into the column
 * and a vertical cross connects to the accordion groups below.
 */
export function SuccessorAxisLabelRow(props: SuccessorAxisLabelRowProps) {
  const {
    title,
    count,
    successorAxisWidthPx = LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
    focusedLayout
  } = props;

  const titleWithCount = formatLinksColumnTitle(title, count);
  const successorAxisLineRightInsetPx = computeSuccessorAxisLineRightInsetPx(
    successorAxisWidthPx,
    focusedLayout
  );
  const successorCrossLeftPx = computeSuccessorCrossLeftPx(
    successorAxisWidthPx,
    focusedLayout
  );

  return (
    <div className="h-10 shrink-0 flex items-center pr-3 pl-0 relative overflow-visible">
      <div
        className="shrink-0 relative h-full"
        style={{ width: successorAxisWidthPx }}
        data-testid="links-column-axis-region"
      >
        <div
          className={classNames(
            "absolute top-1/2 right-0 -translate-y-1/2 h-0 border-0 border-t border-solid pointer-events-none",
            AXIS_LINE_COLOR
          )}
          style={{
            left: LINKS_COLUMN_SUCCESSOR_AXIS_LINE_LEFT_PX,
            right: successorAxisLineRightInsetPx
          }}
          aria-hidden
          data-testid="links-column-axis-line"
        />
        <div
          className={classNames(
            "absolute top-1/2 -translate-y-1/2 w-0 h-16 border-0 border-l border-solid pointer-events-none",
            AXIS_LINE_COLOR
          )}
          style={{ left: successorCrossLeftPx }}
          aria-hidden
          data-testid="links-column-axis-vertical"
        />
      </div>
      <div
        className="text-sm font-medium text-ciq-gray-800 shrink-0 pl-1"
        data-testid="links-column-title"
        style={{ marginLeft: -successorAxisLineRightInsetPx }}
      >
        {titleWithCount}
      </div>
    </div>
  );
}
